/**
 * Global JavaScript for Naddict Shopify Theme
 * Handles common functionality across the site
 */

// Global variables
window.NaddictTheme = {
  cart: {
    items: [],
    total: 0,
    count: 0
  },
  settings: {
    currency: 'GBP',
    locale: 'en'
  }
};

// Utility functions
const Utils = {
  // Format currency
  formatCurrency: function(amount, currency = 'GBP') {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(amount);
  },

  // Debounce function
  debounce: function(func, wait, immediate) {
    let timeout;
    return function executedFunction() {
      const context = this;
      const args = arguments;
      const later = function() {
        timeout = null;
        if (!immediate) func.apply(context, args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func.apply(context, args);
    };
  },

  // Throttle function
  throttle: function(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  },

  // Check if element is in viewport
  isInViewport: function(element) {
    const rect = element.getBoundingClientRect();
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
  },

  // Smooth scroll to element
  smoothScrollTo: function(element, offset = 0) {
    const targetPosition = element.offsetTop - offset;
    window.scrollTo({
      top: targetPosition,
      behavior: 'smooth'
    });
  }
};

// Cart functionality
const Cart = {
  // Initialize cart
  init: function() {
    this.loadCart();
    this.bindEvents();
  },

  // Load cart from Shopify
  loadCart: async function() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      this.updateCartData(cart);
    } catch (error) {
      // Silent fail - error loading cart
    }
  },

  // Update cart data
  updateCartData: function(cart) {
    window.NaddictTheme.cart = {
      items: cart.items || [],
      total: cart.total_price || 0,
      count: cart.item_count || 0
    };
    this.updateCartDisplay();
  },

  // Update cart display
  updateCartDisplay: function() {
    // Update cart count in header
    const cartCountElements = document.querySelectorAll('.cart-count');
    cartCountElements.forEach(element => {
      element.textContent = window.NaddictTheme.cart.count;
    });

    // Update cart total
    const cartTotalElements = document.querySelectorAll('.cart-total');
    cartTotalElements.forEach(element => {
      element.textContent = Utils.formatCurrency(window.NaddictTheme.cart.total / 100);
    });
  },

  // Add item to cart
  addItem: async function(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        const result = await response.json();
        this.loadCart(); // Reload cart data
        this.showAddToCartFeedback();
        return result;
      } else {
        throw new Error('Failed to add item to cart');
      }
    } catch (error) {
      this.showError('Failed to add item to cart');
    }
  },

  // Show add to cart feedback
  showAddToCartFeedback: function() {
    // Create or show feedback element
    let feedback = document.querySelector('.add-to-cart-feedback');
    if (!feedback) {
      feedback = document.createElement('div');
      feedback.className = 'add-to-cart-feedback';
      feedback.innerHTML = '✓ Added to cart!';
      document.body.appendChild(feedback);
    }

    feedback.style.display = 'block';
    setTimeout(() => {
      feedback.style.display = 'none';
    }, 3000);
  },

  // Show error message
  showError: function(message) {
    // Could implement a toast notification system here
  },

  // Bind cart events
  bindEvents: function() {
    // Add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]')) {
        e.preventDefault();
        const variantId = e.target.dataset.variantId;
        const quantity = parseInt(e.target.dataset.quantity) || 1;
        this.addItem(variantId, quantity);
      }
    });
  }
};

// Mobile menu functionality
const MobileMenu = {
  init: function() {
    this.bindEvents();
  },

  bindEvents: function() {
    // Mobile menu toggle
    document.addEventListener('click', (e) => {
      if (e.target.matches('.mobile-menu-toggle')) {
        this.toggle();
      }
    });

    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      const menu = document.querySelector('.main-navigation');
      const toggle = document.querySelector('.mobile-menu-toggle');
      
      if (menu && menu.classList.contains('active') && 
          !menu.contains(e.target) && 
          !toggle.contains(e.target)) {
        this.close();
      }
    });

    // Close menu when clicking on links
    document.addEventListener('click', (e) => {
      if (e.target.matches('.nav-menu a')) {
        this.close();
      }
    });
  },

  toggle: function() {
    const menu = document.querySelector('.main-navigation');
    const toggle = document.querySelector('.mobile-menu-toggle');
    
    if (menu && toggle) {
      menu.classList.toggle('active');
      toggle.classList.toggle('active');
      
      // Prevent body scroll when menu is open
      if (menu.classList.contains('active')) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },

  close: function() {
    const menu = document.querySelector('.main-navigation');
    const toggle = document.querySelector('.mobile-menu-toggle');
    
    if (menu && toggle) {
      menu.classList.remove('active');
      toggle.classList.remove('active');
      document.body.style.overflow = '';
    }
  }
};

// FAQ functionality
const FAQ = {
  init: function() {
    this.bindEvents();
  },

  bindEvents: function() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('.faq-question')) {
        this.toggleAnswer(e.target.closest('.faq-item'));
      }
    });
  },

  toggleAnswer: function(faqItem) {
    const answer = faqItem.querySelector('.faq-answer');
    const arrow = faqItem.querySelector('.faq-arrow');
    
    if (answer && arrow) {
      const isOpen = faqItem.classList.contains('active');
      
      // Close all other FAQ items
      document.querySelectorAll('.faq-item.active').forEach(item => {
        if (item !== faqItem) {
          item.classList.remove('active');
          const otherAnswer = item.querySelector('.faq-answer');
          const otherArrow = item.querySelector('.faq-arrow');
          if (otherAnswer && otherArrow) {
            otherAnswer.style.maxHeight = null;
            otherArrow.style.transform = 'rotate(0deg)';
          }
        }
      });
      
      // Toggle current item
      if (isOpen) {
        faqItem.classList.remove('active');
        answer.style.maxHeight = null;
        arrow.style.transform = 'rotate(0deg)';
      } else {
        faqItem.classList.add('active');
        answer.style.maxHeight = answer.scrollHeight + 'px';
        arrow.style.transform = 'rotate(90deg)';
      }
    }
  }
};

// Carousel functionality
const Carousel = {
  init: function() {
    this.bindEvents();
    this.initializeCarousels();
  },

  bindEvents: function() {
    // Carousel navigation
    document.addEventListener('click', (e) => {
      if (e.target.matches('.carousel-nav--prev')) {
        this.previousSlide(e.target.closest('.carousel-container'));
      }
      if (e.target.matches('.carousel-nav--next')) {
        this.nextSlide(e.target.closest('.carousel-container'));
      }
    });
  },

  initializeCarousels: function() {
    document.querySelectorAll('.carousel-container').forEach(carousel => {
      this.setupCarousel(carousel);
    });
  },

  setupCarousel: function(carousel) {
    const track = carousel.querySelector('.carousel-track');
    const cards = carousel.querySelectorAll('.carousel-card');
    
    if (track && cards.length > 0) {
      let currentIndex = 0;
      const cardWidth = cards[0].offsetWidth;
      const visibleCards = Math.floor(carousel.offsetWidth / cardWidth);
      
      // Set initial position
      this.updateCarouselPosition(track, currentIndex, cardWidth);
      
      // Store carousel data
      carousel.carouselData = {
        track,
        cards,
        currentIndex,
        cardWidth,
        visibleCards
      };
    }
  },

  nextSlide: function(carousel) {
    const data = carousel.carouselData;
    if (data) {
      data.currentIndex = Math.min(data.currentIndex + 1, data.cards.length - data.visibleCards);
      this.updateCarouselPosition(data.track, data.currentIndex, data.cardWidth);
    }
  },

  previousSlide: function(carousel) {
    const data = carousel.carouselData;
    if (data) {
      data.currentIndex = Math.max(data.currentIndex - 1, 0);
      this.updateCarouselPosition(data.track, data.currentIndex, data.cardWidth);
    }
  },

  updateCarouselPosition: function(track, index, cardWidth) {
    const translateX = -index * cardWidth;
    track.style.transform = `translateX(${translateX}px)`;
  }
};

// Smooth scrolling for anchor links
const SmoothScroll = {
  init: function() {
    this.bindEvents();
  },

  bindEvents: function() {
    document.addEventListener('click', (e) => {
      if (e.target.matches('a[href^="#"]')) {
        e.preventDefault();
        const targetId = e.target.getAttribute('href').substring(1);
        const targetElement = document.getElementById(targetId);
        
        if (targetElement) {
          Utils.smoothScrollTo(targetElement, 80); // 80px offset for header
        }
      }
    });
  }
};

// Lazy loading for images
const LazyLoad = {
  init: function() {
    if ('IntersectionObserver' in window) {
      this.setupIntersectionObserver();
    } else {
      this.loadAllImages();
    }
  },

  setupIntersectionObserver: function() {
    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          this.loadImage(img);
          observer.unobserve(img);
        }
      });
    });

    document.querySelectorAll('img[data-src]').forEach(img => {
      imageObserver.observe(img);
    });
  },

  loadImage: function(img) {
    img.src = img.dataset.src;
    img.removeAttribute('data-src');
    img.classList.add('loaded');
  },

  loadAllImages: function() {
    document.querySelectorAll('img[data-src]').forEach(img => {
      this.loadImage(img);
    });
  }
};

// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  // Initialize all modules
  Cart.init();
  MobileMenu.init();
  FAQ.init();
  Carousel.init();
  SmoothScroll.init();
  LazyLoad.init();

  // Add loaded class to body
  document.body.classList.add('loaded');

  // Initialize any other theme-specific functionality
  // Theme initialized successfully
});

// Export for use in other scripts
window.NaddictTheme.Utils = Utils;
window.NaddictTheme.Cart = Cart;
window.NaddictTheme.MobileMenu = MobileMenu;
window.NaddictTheme.FAQ = FAQ;
window.NaddictTheme.Carousel = Carousel;
